/**
 * Utilitaires pour la gestion des dates et semaines ISO 8601
 */

/**
 * Calcule le numéro de semaine ISO 8601 pour une date donnée
 * La norme ISO 8601 définit:
 * - La semaine commence le lundi
 * - La semaine 1 est celle qui contient le premier jeudi de l'année
 * - Une semaine appartient à l'année qui contient le jeudi
 * 
 * @param date Date pour laquelle calculer la semaine (par défaut: aujourd'hui)
 * @returns Numéro de semaine ISO (1-53)
 */
export function getISOWeekNumber(date: Date = new Date()): number {
  // Copier la date pour ne pas modifier l'original
  const target = new Date(date.valueOf());
  
  // Définir au jeudi de la même semaine (ISO 8601 définit la semaine par rapport au jeudi)
  const dayNumber = (target.getDay() + 6) % 7; // Lundi = 0, Dimanche = 6
  target.setDate(target.getDate() - dayNumber + 3); // Jeudi de cette semaine
  
  // Premier jeudi de l'année
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const firstThursdayDay = (firstThursday.getDay() + 6) % 7;
  firstThursday.setDate(firstThursday.getDate() - firstThursdayDay + 3);
  
  // Calculer le nombre de semaines entre les deux jeudis
  const weekNumber = 1 + Math.round(((target.getTime() - firstThursday.getTime()) / 86400000 - 3) / 7);
  
  return weekNumber;
}

/**
 * Obtient l'année ISO de la semaine pour une date donnée
 * Une date peut appartenir à une semaine de l'année précédente ou suivante
 * 
 * @param date Date à vérifier
 * @returns Année ISO de la semaine
 */
export function getISOWeekYear(date: Date = new Date()): number {
  const target = new Date(date.valueOf());
  const dayNumber = (target.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNumber + 3); // Jeudi de cette semaine
  return target.getFullYear();
}

/**
 * Obtient la date du lundi d'une semaine ISO donnée
 * 
 * @param weekNumber Numéro de semaine ISO (1-53)
 * @param year Année
 * @returns Date du lundi de cette semaine
 */
export function getMondayOfISOWeek(weekNumber: number, year: number): Date {
  const jan4 = new Date(year, 0, 4);
  const jan4Day = (jan4.getDay() + 6) % 7; // 0 = lundi, 6 = dimanche
  const mondayOfWeek1 = new Date(year, 0, 4 - jan4Day);
  
  const targetMonday = new Date(mondayOfWeek1);
  targetMonday.setDate(mondayOfWeek1.getDate() + (weekNumber - 1) * 7);
  
  return targetMonday;
}

/**
 * Obtient la plage de dates (lundi-dimanche) pour une semaine ISO
 * 
 * @param weekNumber Numéro de semaine ISO
 * @param year Année
 * @returns Objet avec les dates de début (lundi) et fin (dimanche)
 */
export function getWeekDateRange(weekNumber: number, year: number): { start: Date; end: Date } {
  const monday = getMondayOfISOWeek(weekNumber, year);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  
  return { start: monday, end: sunday };
}

/**
 * Formate une plage de dates de semaine en français
 * 
 * @param weekNumber Numéro de semaine ISO
 * @param year Année
 * @returns Chaîne formatée (ex: "14/10 au 20/10/2025")
 */
export function formatWeekDateRange(weekNumber: number, year: number): string {
  const { start, end } = getWeekDateRange(weekNumber, year);
  
  const startStr = start.toLocaleDateString('fr-FR', { 
    day: '2-digit', 
    month: '2-digit'
  });
  
  const endStr = end.toLocaleDateString('fr-FR', { 
    day: '2-digit', 
    month: '2-digit',
    year: 'numeric'
  });
  
  return `${startStr} au ${endStr}`;
}
